import { FC, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import {
  useFetchEmployee,
  useFetchEmployeeBalances,
} from "src/Pages/Company/Absences/api/employee.queries";
import { useGetAbsenceType } from "src/Pages/Company/Absences/api/absenceTypes.queries";
import transformBalancesToAbsenceTypeOptions from "src/utils/Company/Absences/transformBalancesToAbsenceTypeOptions";
import Title from "src/Pages/Company/Absences/Title/Title";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import AbsenceRequestForm from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestForm";
import EmployeeCard from "src/Pages/Company/Absences/EmployeeCard";
import EmployeeCardSkeleton from "src/Pages/Company/Absences/EmployeeCard/EmployeeCardSkeleton";
import { Loader } from "src/Components/Loader/Loader";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import { toFormAbsenceType } from "./AbsenceRequestCreate";

interface IMatchParams {
  employeeId: string;
}

const AbsenceRequestCreate: FC<RouteComponentProps<IMatchParams>> = ({
  match,
}) => {
  const { employeeId } = match.params;
  const basePath = useBasePath();
  const logStatus = useLogStatus();

  if (!employeeId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No employee ID provided.",
      file: "AbsenceRequestCreate.tsx",
    });
  }

  const [absenceTypeId, setAbsenceTypeId] = useState<string | undefined>(
    undefined
  );

  const {
    data: employee,
    isError: isErrorEmployee,
    isFetching: isFetchingEmployee,
  } = useFetchEmployee(employeeId);

  const {
    data: absenceType,
    isFetching: isFetchingAbsenceType,
    isError: isErrorAbsenceType,
  } = useGetAbsenceType(absenceTypeId);

  const { data: balancesData, isError: isErrorBalances } =
    useFetchEmployeeBalances(employeeId);

  const absenceTypeOptions = useMemo(() => {
    if (!balancesData) return [];

    return transformBalancesToAbsenceTypeOptions(balancesData.balanceDetails);
  }, [balancesData]);

  const handleAbsenceTypeChange = (event: SelectChangeEvent) => {
    setAbsenceTypeId(event.target.value);
  };

  if (isErrorEmployee || isErrorAbsenceType || isErrorBalances)
    return <ErrorDataFetching />;

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Kartoteki nieobecności Pracowników"),
            to: `${basePath}/absences/absence-records`,
          },
          {
            label: __("Nieobecności pracownika"),
            to: `${basePath}/absences/employee/${employeeId}`,
          },
          {
            label: __("Złóż wniosek za pracownika"),
          },
        ]}
      />
      <Title title={__("Złóż wniosek za pracownika")} />
      {isFetchingEmployee ? (
        <EmployeeCardSkeleton />
      ) : (
        <EmployeeCard data={employee} />
      )}
      <Paper sx={{ p: "2rem" }} data-testid="absence-request-employee-create">
        <Fieldset legend={__("Rodzaj wniosku")}>
          <FormControl fullWidth>
            <InputLabel id="absence-type-label" required>
              {__("Nazwa wniosku")}
            </InputLabel>
            <Select
              label={__("Nazwa wniosku")}
              labelId="absence-type-label"
              required
              onChange={handleAbsenceTypeChange}
              data-testid="absence-request-employee-create-field__name"
            >
              {absenceTypeOptions.map((absenceType) => (
                <MenuItem key={absenceType.id} value={absenceType.id}>
                  {absenceType.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Fieldset>
        {isFetchingAbsenceType && <Loader />}
        {employee && absenceType && (
          <AbsenceRequestForm
            key={absenceTypeId}
            absenceType={toFormAbsenceType(absenceType)}
            employeeId={employeeId}
            employeeDailyWorkingHours={employee.dailyWorkingHours}
          />
        )}
      </Paper>
    </>
  );
};

export default AbsenceRequestCreate;
